/**
 * This is our core BigNumber handling code, it will be responsible for ALL BigInt/BigNumber functionality
 */

import type { WeiValue } from "./types"
import BigNumber from "bignumber.js"

export type BigNumberish = string | number | bigint | BigNumber

BigNumber.config({
  DECIMAL_PLACES: 32,
  // ROUNDING_MODE: BigNumber.ROUND_DOWN, // We do NOT want any rounding!
  EXPONENTIAL_AT: [-100, 100],
})

export const DEFAULT_WEI_VALUE = {
  wei: 0n,
  decimals: 18,
} as const

export const makeWeiValue = ({
  wei = 0n,
  decimals = 18,
}: {
  wei?: bigint
  decimals?: number
} = {}): WeiValue => ({
  wei,
  decimals,
})

/**
 * Convert a WeiValue to a BigNumber as the decimal version of itself. i.e. 12345678 with decimals of 6 would be BigNumber(12.345678)
 * @param weiValue - The WeiValue to convert
 * @returns BigNumber representation of the WeiValue including decimals conversion
 */
export const weiValueToBigNumber = (weiValue: WeiValue | null | undefined): BigNumber => {
  if (!weiValue) {
    return new BigNumber(0)
  }
  return new BigNumber(weiValue.wei.toString()).div(new BigNumber(10).pow(weiValue.decimals))
}

/**
 * Convert a WeiValue quantity to a value in USD
 * @param weiValue - The WeiValue to convert
 * @param priceUsd - The price in USD
 * @returns The price in USD
 */
export const weiValueToAmountUsd = (weiValue: WeiValue, priceUsd: number): number => {
  return new BigNumber(weiValue.wei.toString())
    .div(new BigNumber(10).pow(weiValue.decimals))
    .times(new BigNumber(priceUsd))
    .toNumber()
}

/**
 * Convert a WeiValue quantity to a value in USD
 * @param weiValue - The WeiValue to convert
 * @returns The price in USD
 */
export const weiValueToNumber = (weiValue: WeiValue): number => {
  return new BigNumber(weiValue.wei.toString()).div(new BigNumber(10).pow(weiValue.decimals)).toNumber()
}

/**
 * Create a function that accepts a WeiValue and returns a formatted version of that number to display decimal Places
 *
 * @param value - The WeiValue to format
 * @param displayDecimals - The number of decimals to display
 * @returns6
 */
export const formatWeiValue = ({
  weiValue,
  displayDecimals,
}: {
  weiValue: WeiValue
  displayDecimals: number
}): string => {
  if (!weiValue) {
    return "0"
  }

  // console.log("weiValue", weiValue, displayDecimals)

  const weiString = weiValue?.wei?.toString() ?? ""

  // For numbers less than 1 (when wei digits < decimals)
  if (weiString.length <= weiValue.decimals) {
    // For extremely small numbers with high decimals, we need exact precision
    if (weiValue.wei <= 1000n && weiValue.decimals >= 24) {
      const zerosNeeded = weiValue.decimals - weiString.length
      const leadingZeros = "0".repeat(zerosNeeded)
      const fullDecimal = `${leadingZeros}${weiString}`
      return `0.${fullDecimal}`
    }

    // For other cases, keep existing logic
    const extraZeros = weiValue.decimals >= 24 ? 3 : 0
    const zerosNeeded = weiValue.decimals - weiString.length + extraZeros
    const leadingZeros = "0".repeat(zerosNeeded)
    const fullDecimal = `${leadingZeros}${weiString}`

    if (displayDecimals === 0) {
      return "0"
    }

    // For other extremely small numbers, preserve exact number of zeros
    if (weiValue.wei <= 1000n) {
      return `0.${fullDecimal.slice(0, displayDecimals)}`
    }

    // For other small numbers, handle with standard decimal places
    const truncatedDecimal = fullDecimal.padEnd(displayDecimals, "0").slice(0, displayDecimals)
    return `0.${truncatedDecimal}`
  }

  // For numbers greater than or equal to 1
  const position = weiString.length - weiValue.decimals
  const integerPart = weiString.slice(0, position) || "0"
  const decimalPart = weiString.slice(position)

  // For numbers greater than or equal to 1
  if (displayDecimals === 0) {
    return addThousandSeparators(integerPart)
  }

  const formattedInteger = addThousandSeparators(integerPart)
  const truncatedDecimal = decimalPart.padEnd(displayDecimals, "0").slice(0, displayDecimals)

  return `${formattedInteger}.${truncatedDecimal}`
}

export const toBigNumber = (value: BigNumberish): BigNumber => {
  if (value instanceof BigNumber) {
    return value
  }
  return new BigNumber(value.toString())
}

const addThousandSeparators = (integerPart: string): string => {
  return integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}
